<!--
  AvailabilityTimeline: mini week timeline of availabilities - rows Mo–Fr (Sa/So only with windows), shared hour axis that
  auto-fits the data, one bar per window coloured by its label (colours of availabilityCard.helper / TimetableV2 bands; under
  "Alle" a segmented left edge and label chips show every label of a window, windows without label are neutral grey),
  overlapping windows in lanes, times on the bar, beside it when narrow, hidden when there is no free room, empty days as a thin "–" row, today highlighted.
  Hover shows the details as tooltip, a tap / click pins them below as "Ausgewähltes Zeitfenster" (closable). Narrow containers (phone) switch to a
  stacked list per day. Layout: buildTimeline (pure). Props: availabilities (already filtered), filter (colours bars by the
  selected label first), axisFrom (windows the axis fits to, e.g. unfiltered)
-->
<template>
  <div class="avt">
    <div class="avt-grid">
      <div aria-hidden="true" class="avt-axis">
        <span class="avt-axis__day"/>
        <div class="avt-axis__track">
          <span v-for="tick in timeline.ticks" :key="tick.hour" :style="{ left: `${tick.left}%` }" class="avt-axis__tick">{{ tick.hour }}</span>
        </div>
      </div>
      <div v-for="row in timeline.rows" :key="row.day" :class="{ 'avt-row--today': row.isToday, 'avt-row--empty': row.empty }" class="avt-row">
        <span class="avt-row__day">{{ row.short }}</span>
        <div :style="{ height: row.empty ? '12px' : `${row.lanes * LANE - 2}px` }" class="avt-row__track">
          <span v-for="tick in timeline.ticks" :key="tick.hour" :style="{ left: `${tick.left}%` }" class="avt-row__grid"/>
          <span v-if="row.empty" class="avt-row__none">–<span class="avt-sr">nicht verfügbar</span></span>
          <template v-for="bar in row.bars">
            <button :key="bar.key" :class="{ 'avt-bar--active': selected === bar.key, 'avt-bar--nolabel': bar.unlabeled }" :style="barStyle(bar)"
                    :aria-label="bar.details" class="avt-bar" type="button" v-tip="bar.details" @click="toggle(bar.key)">
              <span :style="{ background: edge(bar) }" class="avt-bar__edge"/>
              <span v-if="bar.textAt === 'inside'" class="avt-bar__text">{{ bar.text }}</span>
              <span v-if="showChips && bar.chips.length > 1" aria-hidden="true" class="avt-bar__dots">
                <span v-for="chip in bar.chips" :key="chip.value" :style="{ background: chip.color }" class="avt-chip__dot"/>
              </span>
            </button>
            <span v-if="bar.textAt === 'after' || bar.textAt === 'before'" :key="bar.key + 't'" :style="outsideStyle(bar)" aria-hidden="true"
                  class="avt-bar__outside">{{ bar.text }}</span>
          </template>
        </div>
      </div>
    </div>
    <ul class="avt-list">
      <li v-for="row in timeline.rows" :key="row.day" :class="{ 'avt-list__row--today': row.isToday }" class="avt-list__row">
        <span class="avt-list__day">{{ row.short }}</span>
        <span v-if="row.empty" class="avt-list__none">–</span>
        <span v-else class="avt-list__items">
          <button v-for="bar in row.bars" :key="bar.key" :aria-label="bar.details" :aria-pressed="String(selected === bar.key)" class="avt-list__item"
                  type="button" @click="toggle(bar.key)">
            <span v-if="!showChips" :style="{ background: bar.color }" class="avt-list__dot"/>
            <span class="avt-list__time">{{ bar.time }}</span>
            <template v-if="showChips">
              <span v-for="chip in bar.chips" :key="chip.value" class="avt-chip"><span :style="{ background: chip.color }" class="avt-chip__dot"/>{{ chip.short }}</span>
              <span v-if="bar.unlabeled && labelsUsed" class="avt-chip avt-chip--none"><span class="avt-chip__dot"/>ohne Label</span>
            </template>
          </button>
        </span>
      </li>
    </ul>
    <div v-if="selectedBar" :style="{ '--avt-color': selectedBar.color }" aria-live="polite" class="avt-details">
      <div class="avt-details__head">
        <span class="avt-details__title">Ausgewähltes Zeitfenster</span>
        <button aria-label="Auswahl schließen" class="avt-details__close" type="button" @click="selected = ''">
          <v-icon x-small>mdi-close</v-icon>
        </button>
      </div>
      <div class="avt-details__time">{{ selectedText }}</div>
      <div v-if="selectedBar.chips.length || labelsUsed" class="avt-details__chips">
        <span v-for="chip in selectedBar.chips" :key="chip.value" class="avt-chip"><span :style="{ background: chip.color }" class="avt-chip__dot"/>{{ chip.text }}</span>
        <span v-if="selectedBar.unlabeled" class="avt-chip avt-chip--none"><span class="avt-chip__dot"/>ohne Label</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {buildTimeline, CardAvailability, edgeBackground, LabelFilter, Timeline, TimelineBar, weekdayOf} from './availabilityCard.helper';

const LANE = 20;

export default Vue.extend({
  props: {
    availabilities: {type: Array as () => CardAvailability[], required: true},
    filter: {type: String as () => LabelFilter, default: 'all'},
    /** false: the data has no labels at all (Schüler:innen) - no "ohne Label" chips */
    labelsUsed: {type: Boolean, default: true},
    /** Windows the hour axis fits to (default: the shown ones) */
    axisFrom: {type: Array as () => CardAvailability[] | null, default: null},
  },
  data: () => ({
    LANE,
    selected: '' as string,
    /** Measured width of the bar track (text fitting), updated by a ResizeObserver */
    trackWidth: 400,
    observer: null as ResizeObserver | null,
  }),
  computed: {
    timeline(): Timeline {
      return buildTimeline(this.availabilities, {
        today: weekdayOf(new Date()), filter: this.filter as LabelFilter, axisItems: this.axisFrom || undefined, trackWidthPx: this.trackWidth,
        labelsUsed: this.labelsUsed,
      });
    },
    /** "Alle": every window shows all its labels; under a label filter one colour is enough */
    showChips(): boolean {
      return this.filter === 'all';
    },
    selectedText(): string {
      if (!this.selectedBar) {
        return '';
      }
      const row = this.timeline.rows.find((el) => el.bars.includes(this.selectedBar as TimelineBar));
      return `${row ? row.text : ''} · ${this.selectedBar.time} Uhr`;
    },
    selectedBar(): TimelineBar | undefined {
      for (const row of this.timeline.rows) {
        const bar = row.bars.find((el) => el.key === this.selected);
        if (bar) {
          return bar;
        }
      }
      return undefined;
    },
  },
  methods: {
    edge(bar: TimelineBar): string {
      return edgeBackground(bar.edgeColors);
    },
    toggle(key: string) {
      this.selected = this.selected === key ? '' : key;
    },
    barStyle(bar: TimelineBar): Record<string, string> {
      return {
        left: `${bar.left}%`,
        width: `max(${bar.width}%, 4px)`,
        top: `${bar.lane * LANE}px`,
        '--avt-color': bar.color,
      };
    },
    outsideStyle(bar: TimelineBar): Record<string, string> {
      const top = `${bar.lane * LANE}px`;
      return bar.textAt === 'after'
        ? {left: `calc(${bar.left + bar.width}% + 4px)`, top}
        : {right: `calc(${100 - bar.left}% + 4px)`, top};
    },
  },
  watch: {
    availabilities() {
      this.selected = '';
    },
  },
  mounted() {
    const measure = () => {
      const width = (this.$el as HTMLElement).clientWidth;
      // day column (26px) + gap (6px)
      if (width > 0) {
        this.trackWidth = Math.max(80, width - 32);
      }
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(measure);
      this.observer.observe(this.$el as HTMLElement);
    }
  },
  beforeDestroy() {
    this.observer?.disconnect();
  },
});
</script>

<style scoped>
.avt {
  container-type: inline-size;
  margin-top: 6px;
}

.avt-grid {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.avt-axis, .avt-row {
  display: grid;
  grid-template-columns: 26px 1fr;
  align-items: center;
  column-gap: 6px;
}

.avt-axis__track {
  position: relative;
  height: 14px;
}

.avt-axis__tick {
  position: absolute;
  transform: translateX(-50%);
  font-size: 10px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
  color: rgba(15, 23, 42, .45);
}

.avt-row {
  padding: 2px 0;
  border-radius: 6px;
}

.avt-row--today {
  background: rgba(25, 118, 210, .06);
}

.avt-row__day {
  padding-left: 4px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(15, 23, 42, .6);
}

.avt-row--today .avt-row__day {
  color: #1565C0;
}

.avt-row__track {
  position: relative;
  min-width: 0;
}

.avt-row__grid {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: rgba(15, 23, 42, .06);
}

.avt-row--empty .avt-row__track::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 18px;
  right: 0;
  border-top: 1px dashed rgba(15, 23, 42, .14);
}

.avt-row__none {
  position: absolute;
  top: -2px;
  left: 4px;
  padding: 0 4px;
  font-size: 11px;
  line-height: 16px;
  color: rgba(15, 23, 42, .35);
}

.avt-bar {
  position: absolute;
  height: 18px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0 5px 0 8px;
  overflow: hidden;
  border: 0;
  border-radius: 4px;
  background: rgba(15, 23, 42, .07);
  background: color-mix(in srgb, var(--avt-color) 20%, transparent);
  color: rgba(15, 23, 42, .85);
  font: inherit;
  cursor: pointer;
  box-sizing: border-box;
}

.avt-bar:hover, .avt-bar--active {
  background: color-mix(in srgb, var(--avt-color) 32%, transparent);
}

.avt-bar--active {
  outline: 2px solid var(--avt-color);
  outline-offset: 0;
}

.avt-bar__text, .avt-bar__outside {
  font-size: 11px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.avt-bar__text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.avt-bar__outside {
  position: absolute;
  color: rgba(15, 23, 42, .7);
  pointer-events: none;
}



.avt-list {
  display: none;
  margin: 0;
  padding: 0;
  list-style: none;
}

.avt-list__row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 4px;
  border-radius: 6px;
  font-size: 12px;
}

.avt-list__row--today {
  background: rgba(25, 118, 210, .06);
}

.avt-list__day {
  flex: 0 0 22px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(15, 23, 42, .6);
}

.avt-list__row--today .avt-list__day {
  color: #1565C0;
}

.avt-list__none {
  color: rgba(15, 23, 42, .35);
}

.avt-list__items {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
}

.avt-list__item {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px 4px;
  padding: 0;
  text-align: left;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.avt-list__dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-right: 5px;
  border-radius: 50%;
}

.avt-details {
  margin-top: 6px;
  padding: 6px 8px 6px 10px;
  border-left: 3px solid var(--avt-color);
  border-radius: 6px;
  background: rgba(15, 23, 42, .04);
  font-size: 12px;
  color: rgba(15, 23, 42, .8);
}

.avt-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* "Alle": one dot per label at the end of the bar (clipped when the bar is too narrow; edge + tooltip stay) */
.avt-bar__dots {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 2px;
  margin-left: auto;
}

/* Segmented left edge: one segment per label under "Alle", one colour under a label filter */
.avt-bar__edge {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
}

.avt-bar--nolabel {
  background: rgba(144, 164, 174, .14);
  outline: 1px dashed rgba(96, 125, 139, .55);
  outline-offset: -1px;
}

.avt-list__time {
  font-variant-numeric: tabular-nums;
}

.avt-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: rgba(15, 23, 42, .05);
  font-size: 11px;
  color: rgba(15, 23, 42, .75);
  white-space: nowrap;
}

.avt-chip__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #90A4AE;
}

.avt-chip--none {
  border: 1px dashed rgba(96, 125, 139, .55);
  background: transparent;
  color: rgba(15, 23, 42, .55);
}

.avt-details__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.avt-details__title {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: rgba(15, 23, 42, .5);
}

.avt-details__close {
  display: inline-flex;
  padding: 2px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: inherit;
  cursor: pointer;
}

.avt-details__time {
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.avt-details__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.theme--dark .avt-chip {
  background: rgba(255, 255, 255, .08);
  color: rgba(255, 255, 255, .8);
}

.theme--dark .avt-chip--none {
  background: transparent;
  color: rgba(255, 255, 255, .55);
}

.theme--dark .avt-details__title {
  color: rgba(255, 255, 255, .5);
}

@container (max-width: 340px) {
  .avt-grid {
    display: none;
  }

  .avt-list {
    display: block;
  }
}

.theme--dark .avt-axis__tick, .theme--dark .avt-row__day, .theme--dark .avt-list__day {
  color: rgba(255, 255, 255, .55);
}

.theme--dark .avt-row--today, .theme--dark .avt-list__row--today {
  background: rgba(144, 202, 249, .1);
}

.theme--dark .avt-row--today .avt-row__day, .theme--dark .avt-list__row--today .avt-list__day {
  color: #90CAF9;
}

.theme--dark .avt-row__grid {
  background: rgba(255, 255, 255, .07);
}

.theme--dark .avt-row--empty .avt-row__track::before {
  border-top-color: rgba(255, 255, 255, .16);
}

.theme--dark .avt-row__none {
  color: rgba(255, 255, 255, .35);
}

.theme--dark .avt-bar {
  background: color-mix(in srgb, var(--avt-color) 26%, transparent);
  color: rgba(255, 255, 255, .9);
}

.theme--dark .avt-bar:hover, .theme--dark .avt-bar--active {
  background: color-mix(in srgb, var(--avt-color) 40%, transparent);
}

.theme--dark .avt-bar__outside, .theme--dark .avt-details {
  color: rgba(255, 255, 255, .75);
}

.theme--dark .avt-list__none {
  color: rgba(255, 255, 255, .35);
}

.theme--dark .avt-details {
  background: rgba(255, 255, 255, .06);
}
</style>
